import React, { useState, useEffect, useRef } from "react";
import { Product, BumpProduct, Theme } from "../shared/types";
import useTheme from "../hooks/useTheme";
import { ThemeProvider } from "../components/ThemeContext";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import type { StripeElementLocale } from "@stripe/stripe-js";
import PaymentForm from "./PaymentForm";
import { useProductSelectionStore, createProductSelectionStore } from "../store/product-selection-store";

interface OrderFormProps {
  data: {
    theme?: Theme;
    bumpLast?: boolean;
    productDisplayType: string;
    bumpHeadline?: string;
    ctaButtonText?: string;
    tos_url?: string;
    products: Product[];
    bumpProducts?: BumpProduct[];
    clientSecret: string;
    paymentIntentId: string;
    currency?: string;
    locale?: string;
  };
  componentId?: string; // Optional to maintain backward compatibility
}

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY ?? "");

const OrderForm: React.FC<OrderFormProps> = ({ data, componentId = 'default' }) => {
  const { clientSecret, paymentIntentId, products, bumpProducts = [] } = data;
  
  // Use the namespaced product selection store based on component ID
  const useStore = createProductSelectionStore(componentId);
  
  // Get store state and actions
  const {
    // State
    error,
    isInitialized,
    selectedProduct,
    selectedProductIndex,
    finalPrice,
    skuPriceMap,
    skuDetailsMap,
    
    // Actions
    initialize,
    reset,
    selectProduct,
    toggleBump
  } = useStore();
  
  // Use a ref to track component mount state for safe cleanup
  const isMounted = useRef(true);
  
  // Initialize the store with products data
  useEffect(() => {
    // Only initialize if not already initialized
    if (!isInitialized) {
      initialize(products, bumpProducts);
    }
    
    // Cleanup when component unmounts - not during re-renders
    return () => {
      // Only perform reset if this is a genuine unmount, not a re-render
      if (isMounted.current) {
        // Set the ref to false right before resetting to prevent loops
        isMounted.current = false;
        // Use setTimeout to defer the reset to the next tick, outside of React's render phase
        setTimeout(() => {
          reset();
        }, 0);
      }
    };
  }, [products, bumpProducts, initialize, reset, isInitialized]);
  
  // Rename the store actions to match the previous API
  const handleProductChange = selectProduct;
  const handleBumpChange = toggleBump;

  const stripeOptions = {
    clientSecret,
    locale: (data.locale as StripeElementLocale) ?? "en",
    // mode: "payment",
    // currency: data.currency ?? "usd",
  };
  
  const [termsAccepted, setTermsAccepted] = useState(false);
  const { primaryButton } = useTheme(data.theme ?? "default");
  
  // Ensure selectedProductIndex is always treated as a number (not null)
  const validSelectedProductIndex: number = selectedProductIndex ?? 0;
  
  // Show error if store initialization failed
  if (error) {
    return (
      <div className="p-4 text-red-600 bg-red-100 rounded">
        <h3 className="font-bold">Error loading products</h3>
        <p>{error}</p>
      </div>
    );
  }
  
  // Show loading state while initializing
  if (!isInitialized) {
    return (
      <div className="p-4">
        <p>Loading products...</p>
      </div>
    );
  }
  
  return (
    <ThemeProvider theme={data.theme ?? "default"}>
      <Elements stripe={stripePromise} options={stripeOptions}>
        <PaymentForm
          data={data}
          products={products}
          productDisplayType={data.productDisplayType}
          bumpProducts={bumpProducts}
          bumpLast={data.bumpLast}
          clientSecret={clientSecret}
          paymentIntentId={paymentIntentId}
          skuDetailsMap={skuDetailsMap}
          finalPrice={finalPrice}
          termsAccepted={termsAccepted}
          setTermsAccepted={setTermsAccepted}
          primaryButton={primaryButton}
          selectedProduct={selectedProduct}
          selectedProductIndex={validSelectedProductIndex}
          handleProductChange={handleProductChange}
          handleBumpChange={handleBumpChange}
          skuPriceMap={skuPriceMap}
          theme={data.theme ?? "default"}
        />
      </Elements>
    </ThemeProvider>
  );
};

export default OrderForm;
